import React, { useState, useRef } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { ResultData } from "../types";
import niesbudLogo from "../niesbudlogo.png";
import ministryLogo from "../MinistryOfSkillDevLogo.png";
import skillIndiaLogo from "../SkillIndiaLogo.png";

interface ScoreCardProps {
    data: ResultData;
    onViewAnswers: () => void;       // ✅ renamed — opens DetailedScoreCard
    isLoadingAnswers?: boolean;      // ✅ loading state for view button
}

const ScoreCard: React.FC<ScoreCardProps> = ({
    data,
    onViewAnswers,
    isLoadingAnswers = false,
}) => {
    const isPassed = data.status === "PASSED";
    const cardRef = useRef<HTMLDivElement>(null);
    const [isDownloading, setIsDownloading] = useState(false);

    // ✅ Download the score card itself as PDF
    const downloadScoreCardPDF = async () => {
        if (!cardRef.current) return;
        setIsDownloading(true);

        try {
            const element = cardRef.current;
            const rect = element.getBoundingClientRect();
            const contentWidth = Math.max(rect.width, 800);

            const canvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: "#ffffff",
                width: contentWidth,
                windowWidth: contentWidth,
                height: element.scrollHeight,
                windowHeight: element.scrollHeight,
                onclone: (clonedDoc, clonedElement) => {
                    const htmlEl = clonedElement as HTMLElement;
                    htmlEl.style.width = contentWidth + "px";
                    htmlEl.style.maxWidth = contentWidth + "px";
                    htmlEl.style.background = "#ffffff";
                    htmlEl.style.padding = "32px";
                    htmlEl.style.margin = "0";

                    let parent = htmlEl.parentElement;
                    while (parent && parent !== clonedDoc.body) {
                        parent.style.position = "static";
                        parent.style.width = "auto";
                        parent.style.maxWidth = "none";
                        parent.style.display = "block";
                        parent.style.overflow = "visible";
                        parent = parent.parentElement;
                    }

                    // Hide buttons during capture
                    const buttons = clonedDoc.querySelectorAll(
                        '[data-pdf-hide="true"]'
                    );
                    buttons.forEach((b) => {
                        (b as HTMLElement).style.display = "none";
                    });
                },
            });

            const imgData = canvas.toDataURL("image/jpeg", 0.95);
            const pdf = new jsPDF("p", "mm", "a4");
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const imgWidth = canvas.width;
            const imgHeight = canvas.height;
            const ratio = pdfWidth / imgWidth;
            const scaledHeight = imgHeight * ratio;

            let heightLeft = scaledHeight;
            let position = 0;

            pdf.addImage(imgData, "JPEG", 0, position, pdfWidth, scaledHeight);
            heightLeft -= pdfHeight;

            while (heightLeft > 0) {
                position = heightLeft - scaledHeight;
                pdf.addPage();
                pdf.addImage(
                    imgData,
                    "JPEG",
                    0,
                    position,
                    pdfWidth,
                    scaledHeight
                );
                heightLeft -= pdfHeight;
            }

            pdf.save(`ScoreCard_${data.niesbudRegNo}.pdf`);
        } catch (error) {
            console.error("Error generating PDF:", error);
            alert("Failed to download PDF: " + (error as Error).message);
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <div className="bg-white p-8 shadow-2xl border-2 border-blue-600 rounded-2xl">
            {/* ✅ Content to capture in PDF */}
            <div ref={cardRef}>
                {/* Header */}
                <div className="flex justify-between items-center mb-3 pb-5 border-b-2 border-slate-200">
                    <img
                        src={skillIndiaLogo}
                        alt="Skill India"
                        className="h-14 w-auto"
                        crossOrigin="anonymous"
                    />
                    <img
                        src={ministryLogo}
                        alt="Ministry"
                        className="h-14 w-auto"
                        crossOrigin="anonymous"
                    />
                    <img
                        src={niesbudLogo}
                        alt="NIESBUD"
                        className="h-14 w-auto"
                        crossOrigin="anonymous"
                    />
                </div>

                <h1 className="text-2xl font-bold text-slate-800 text-center mb-3">
                    Score Card
                </h1>

                {/* Candidate Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-8 p-5 bg-slate-50 rounded-xl border-2 border-blue-600">
                    <InfoItem label="NIESBUD Reg. No" value={data.niesbudRegNo} />
                    <InfoItem label="Candidate Name" value={data.candidateName} />
                    <InfoItem
                        label="Father Name"
                        value={data.candidateFatherName}
                    />
                    <InfoItem label="Program Name" value={data.programName} />
                    <InfoItem label="Batch Name" value={data.batchName} />
                    <InfoItem label="Issued by" value="Niesbud" />
                </div>

                {/* Score Summary */}
                <div className="flex flex-wrap items-center justify-around gap-8 mb-8 p-6 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border-2 border-blue-600">
                    <div className="relative w-40 h-40">
                        <svg
                            viewBox="0 0 36 36"
                            className="w-full h-full -rotate-90"
                        >
                            <path
                                className="fill-none stroke-slate-200"
                                strokeWidth="2.5"
                                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            />
                            <path
                                className={`fill-none stroke-[2.5] stroke-linecap-round ${isPassed
                                        ? "stroke-green-500"
                                        : "stroke-red-500"
                                    }`}
                                strokeDasharray={`${data.percentageScored}, 100`}
                                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <span className="text-3xl font-bold text-slate-800">
                                {data.percentageScored}%
                            </span>
                            <span className="text-xs text-slate-500">
                                Score
                            </span>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-5">
                        <Stat
                            value={data.totalScoreSecured}
                            label="Total Score"
                        />
                        <Stat value={data.totalScore} label="Max Score" />
                    </div>
                </div>

                {/* Footer inside capture */}
                <div className="mt-8 pt-5 border-t border-dashed border-slate-300 text-center text-xs text-slate-400">
                    <p>
                        This is a computer-generated score card. No signature
                        required.
                    </p>
                    <p className="mt-1">Score ID: {data.id}</p>
                </div>
            </div>

            {/* ✅ TWO BUTTONS — hidden during PDF capture */}
            <div
                data-pdf-hide="true"
                className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3"
            >
                {/* Button 1: Download PDF */}
                <button
                    onClick={downloadScoreCardPDF}
                    disabled={isDownloading}
                    className="py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium transition"
                >
                    {isDownloading ? (
                        <>
                            <span className="animate-spin">⏳</span>
                            Downloading...
                        </>
                    ) : (
                        <>
                            <span>📥</span>
                            Download Score Card PDF
                        </>
                    )}
                </button>

                {/* Button 2: View Answer Sheet */}
                <button
                    onClick={onViewAnswers}
                    disabled={isLoadingAnswers}
                    className="py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium transition"
                >
                    {isLoadingAnswers ? (
                        <>
                            <span className="animate-spin">⏳</span>
                            Loading...
                        </>
                    ) : (
                        <>
                            <span>👁️</span>
                            View Answer Sheet
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

const InfoItem: React.FC<{ label: string; value: string }> = ({
    label,
    value,
}) => (
    <div>
        <label className="text-xs text-slate-500 uppercase tracking-wide font-semibold">
            {label}
        </label>
        <p className="text-sm text-slate-800 font-medium mt-1">{value}</p>
    </div>
);

const Stat: React.FC<{ value: number | string; label: string }> = ({
    value,
    label,
}) => (
    <div className="text-center">
        <span className="block text-2xl font-bold text-indigo-500">
            {value}
        </span>
        <span className="text-xs text-slate-500 mt-1">{label}</span>
    </div>
);

export default ScoreCard;